import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Home,
  CalendarRange,
  CalendarPlus,
  List,
  Music,
  Users,
  UserCircle,
  Settings,
  LayoutDashboard,
  BarChart3,
  MapPin,
  ChevronLeft,
  ChevronRight,
  User,
  Bed,
  Briefcase,
  Clock,
  ListMusic,
  ListOrdered,
  Mic,
  Navigation,
  Radio,
  GraduationCap,
  Wrench,
  Route,
  Sparkles,
} from 'lucide-react';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import { MobileNav, MobileHeader, DesktopTopNav } from '@/components/navigation';
import { MiniCalendar } from '@/components/dashboard/MiniCalendar';
import SongDetailsWidget from '@/components/ui/SongDetailsWidget';

interface ModernLayoutProps {
  children: React.ReactNode;
}

interface HeaderNavItem {
  id: string;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface SidebarFunction {
  id: string;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  href: string;
}

const SCROLL_STORAGE_KEY = 'luma-scroll-positions';

const headerNavigation: HeaderNavItem[] = [];

function UpcomingEventsPlaceholder({ limit }: { limit: number }) {
  void limit;
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 text-center py-4">No upcoming events</p>
    </div>
  );
}

export const ModernLayout: React.FC<ModernLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [activeHeaderNav, setActiveHeaderNav] = useState<string>('dashboard');
  const isRestoringRef = useRef(false);
  const lastPathRef = useRef(location.pathname);

  useEffect(() => {
    const path = location.pathname;

    if (path !== lastPathRef.current) {
      lastPathRef.current = path;
      return;
    }

    const restoreScrollPosition = () => {
      try {
        const stored = localStorage.getItem(SCROLL_STORAGE_KEY);
        if (stored) {
          const positions = JSON.parse(stored);
          const savedPosition = positions[path];
          if (savedPosition !== undefined) {
            isRestoringRef.current = true;
            window.scrollTo(0, savedPosition);
            requestAnimationFrame(() => {
              isRestoringRef.current = false;
            });
          }
        }
      } catch (e) {
        console.error('Failed to restore scroll position:', e);
      }
    };

    restoreScrollPosition();

    const handleBeforeUnload = () => {
      try {
        const stored = localStorage.getItem(SCROLL_STORAGE_KEY);
        const positions = stored ? JSON.parse(stored) : {};
        positions[path] = window.scrollY;
        localStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
      } catch (e) {
        console.error('Failed to save scroll position:', e);
      }
    };

    const handleScroll = () => {
      if (!isRestoringRef.current) {
        try {
          const stored = localStorage.getItem(SCROLL_STORAGE_KEY);
          const positions = stored ? JSON.parse(stored) : {};
          positions[path] = window.scrollY;
          localStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
        } catch (e) {
          console.error('Failed to save scroll position:', e);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const [leftSidebarOpen, setLeftSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('modernLayout_leftSidebarOpen');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [rightSidebarOpen, setRightSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('modernLayout_rightSidebarOpen');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const [activePanel, setActivePanel] = useState<'calendar' | 'events' | 'song' | null>(null);

  const handleLeftSidebarToggle = (newState: boolean) => {
    setLeftSidebarOpen(newState);
    localStorage.setItem('modernLayout_leftSidebarOpen', JSON.stringify(newState));
  };

  const handleRightSidebarToggle = (newState: boolean) => {
    setRightSidebarOpen(newState);
    localStorage.setItem('modernLayout_rightSidebarOpen', JSON.stringify(newState));
  };

  const getActiveNavFromPath = (pathname: string): string => {
    if (pathname.startsWith('/events')) return 'events';
    if (pathname.startsWith('/songs')) return 'songs';
    if (pathname.startsWith('/playlists')) return 'songs';
    if (pathname.startsWith('/tours')) return 'events';
    if (pathname.startsWith('/venues')) return 'venues';
    if (pathname.startsWith('/accommodations')) return 'accommodations';
    if (pathname.startsWith('/riders')) return 'riders';
    if (pathname.startsWith('/contacts') || pathname.startsWith('/collaborators')) return 'contacts';
    if (pathname.startsWith('/ensembles')) return 'ensembles';
    if (pathname.startsWith('/equipment')) return 'equipment';
    if (pathname.startsWith('/analytics')) return 'analytics';
    if (pathname.startsWith('/calendar')) return 'calendar';
    if (pathname.startsWith('/settings')) return 'settings';
    if (pathname.startsWith('/ai/tour-planning')) return 'ai-tour-planning';
    return 'dashboard';
  };

  useEffect(() => {
    const activeNav = getActiveNavFromPath(location.pathname);
    setActiveHeaderNav(activeNav);
  }, [location.pathname]);

  const handleHeaderNavigation = (navId: string) => {
    setActiveHeaderNav(navId);
    switch (navId) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'events':
        navigate('/events');
        break;
      case 'songs':
        navigate('/songs');
        break;
      case 'playlists':
        navigate('/playlists');
        break;
      case 'setlists':
        navigate('/setlists');
        break;
      case 'ensembles':
        navigate('/ensembles');
        break;
      case 'venues':
        navigate('/venues');
        break;
      case 'accommodations':
        navigate('/accommodations');
        break;
      case 'riders':
        navigate('/riders');
        break;
      case 'contacts':
      case 'collaborators':
        navigate('/contacts');
        break;
      case 'ai-tour-planning':
        navigate('/ai/tour-planning');
        break;
      default:
        break;
    }
  };

  function getSidebarFunctions(activeNav: string): SidebarFunction[] {
    switch (activeNav) {
      case 'venues':
        return [
          { id: 'all-venues', name: 'All Venues', icon: MapPin, href: '/venues' },
        ];
      case 'accommodations':
        return [
          { id: 'all-accommodations', name: 'All Accommodations', icon: Bed, href: '/accommodations' },
        ];
      case 'riders':
        return [
          { id: 'all-riders', name: 'All Riders', icon: Briefcase, href: '/riders' },
        ];
      case 'contacts':
      case 'collaborators':
        return [
          { id: 'all-contacts', name: 'All Contacts', icon: Users, href: '/contacts' },
        ];
      case 'ensembles':
        return [
          { id: 'all-ensembles', name: 'All Ensembles', icon: UserCircle, href: '/ensembles' },
        ];
      case 'equipment':
        return [
          { id: 'all-equipment', name: 'All Equipment', icon: Wrench, href: '/equipment' },
        ];
      case 'analytics':
        return [
          { id: 'analytics-overview', name: 'Overview', icon: BarChart3, href: '/analytics' },
        ];
      case 'settings':
        return [
          { id: 'settings-page', name: 'Settings', icon: Settings, href: '/settings' },
        ];
      case 'ai-tour-planning':
        return [
          { id: 'ai-tour-planning', name: 'Tour Planning', icon: Route, href: '/ai/tour-planning' },
        ];
      default:
        return [];
    }
  }

  const sidebarFunctions = getSidebarFunctions(activeHeaderNav);

  if (isMobile) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        <MobileHeader onMenuClick={() => setMobileNavOpen(true)} />
        <MobileNav isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        <main className="flex-1 overflow-y-auto pt-14 px-4 py-6">
          {children}
        </main>
      </div>
    );
  }

  if (isTablet) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="w-48 flex-shrink-0 pt-14">
          {sidebarFunctions.map((func) => {
            const Icon = func.icon;
            const isActive = location.pathname === func.href ||
              (func.href !== '/' && location.pathname.startsWith(func.href));
            return (
              <Link
                key={func.id}
                to={func.href}
                className={`flex items-center px-3 py-3 text-sm font-medium ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5 mr-3" />
                {func.name}
              </Link>
            );
          })}
        </div>
        <main className="flex-1 overflow-y-auto pt-14 px-4 py-6">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 flex-col md:flex-row">
      <DesktopTopNav
        activeNav={activeHeaderNav}
        onNavigate={handleHeaderNavigation}
        leftSidebarOpen={leftSidebarOpen}
        onLeftSidebarToggle={handleLeftSidebarToggle}
      />

      <div className="flex flex-1 pt-16 overflow-hidden">
        <div className={`${leftSidebarOpen ? 'w-64' : 'w-16'} transition-all duration-300 bg-white border-r border-gray-200 shadow-sm hidden lg:flex lg:flex-col relative z-30`}>
          <div className="flex items-center justify-between h-16 px-3 border-b border-gray-200 flex-shrink-0">
            {leftSidebarOpen && (
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide truncate">
                {headerNavigation.find((nav: HeaderNavItem) => nav.id === activeHeaderNav)?.name}
              </h3>
            )}
            <button
              onClick={() => handleLeftSidebarToggle(!leftSidebarOpen)}
              className={`p-2 rounded-md text-gray-400 hover:text-gray-600 transition-colors ${!leftSidebarOpen ? 'mx-auto' : ''}`}
              title={leftSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {leftSidebarOpen ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>
          </div>

          <nav className="p-2 space-y-1 flex-1 overflow-y-auto overflow-x-visible">
            {sidebarFunctions.map((func) => {
              const Icon = func.icon;
              const isActive = location.pathname === func.href ||
                (func.href !== '/' && location.pathname.startsWith(func.href));
              return (
                <Link
                  key={func.id}
                  to={func.href}
                  className={`flex items-center ${leftSidebarOpen ? 'px-3' : 'px-2 justify-center'} py-2 text-sm font-medium rounded-lg transition-colors group relative ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  title={!leftSidebarOpen ? func.name : undefined}
                >
                  <Icon className={`${leftSidebarOpen ? 'w-4 h-4 mr-3' : 'w-5 h-5'} flex-shrink-0 ${isActive ? 'text-primary-600' : ''}`} />
                  {leftSidebarOpen && <span className="truncate">{func.name}</span>}

                  {!leftSidebarOpen && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-[100] transition-opacity duration-75 delay-0">
                      {func.name}
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        <main className="flex-1 overflow-y-auto py-6 px-4 sm:px-6 lg:px-8">
          {children}
        </main>

        <div className={`${rightSidebarOpen ? 'w-80' : 'w-16'} transition-all duration-300 bg-gray-50 border-l border-gray-200 shadow-sm hidden lg:flex lg:flex-col relative z-30`}>
          <div className="h-16 px-3 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
            <button
              onClick={() => handleRightSidebarToggle(!rightSidebarOpen)}
              className={`p-2 rounded-md text-gray-400 hover:text-gray-600 transition-colors ${!rightSidebarOpen ? 'mx-auto' : ''}`}
              title={rightSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {rightSidebarOpen ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
            {rightSidebarOpen && (
              <h2 className="text-sm font-semibold text-gray-900">Info</h2>
            )}
          </div>

          {rightSidebarOpen ? (
            <div className="p-4 flex-1 overflow-y-auto space-y-6">
              <div>
                <MiniCalendar />
              </div>
              <div className="border-t border-gray-200 pt-4">
                <UpcomingEventsPlaceholder limit={5} />
              </div>
              <div className="border-t border-gray-200 pt-4">
                <SongDetailsWidget />
              </div>
            </div>
          ) : (
            <div className="p-2 flex-1 overflow-y-auto space-y-4">
              <div className="group/widget relative">
                <button
                  onMouseEnter={() => setActivePanel('calendar')}
                  className="w-full p-2 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  <CalendarRange className="h-5 w-5" />
                </button>
                <div className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/widget:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-75 delay-0">
                  Calendar
                </div>
              </div>

              <div className="group/widget relative">
                <button
                  onMouseEnter={() => setActivePanel('events')}
                  className="w-full p-2 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  <Clock className="h-5 w-5" />
                </button>
                <div className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/widget:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-75 delay-0">
                  Upcoming Events
                </div>
              </div>

              <div className="group/widget relative">
                <button
                  onMouseEnter={() => setActivePanel('song')}
                  className="w-full p-2 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors flex items-center justify-center"
                >
                  <Music className="h-5 w-5" />
                </button>
                <div className="absolute right-full mr-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover/widget:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-75 delay-0">
                  Song Details
                </div>
              </div>
            </div>
          )}

          {!rightSidebarOpen && activePanel && (
            <div
              className="fixed right-16 top-16 bottom-0 w-80 bg-white border-l border-gray-200 shadow-2xl z-[200] transform transition-transform duration-300 ease-out"
              onMouseLeave={() => setActivePanel(null)}
            >
              <div className="h-full overflow-y-auto p-4 space-y-6">
                {activePanel === 'calendar' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <CalendarRange className="h-4 w-4 mr-2" />
                      Calendar
                    </h3>
                    <MiniCalendar />
                  </div>
                )}

                {activePanel === 'events' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Upcoming Events
                    </h3>
                    <UpcomingEventsPlaceholder limit={5} />
                  </div>
                )}

                {activePanel === 'song' && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Music className="h-4 w-4 mr-2" />
                      Now Playing
                    </h3>
                    <SongDetailsWidget />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default ModernLayout;
